import { useRef } from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import { EmployeeBlocksEmployeeFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import TaxResidenceForm from 'modules/employees/lib/forms/Personal/TaxResidence'

import BasicForm from './BasicForm'
import ExtraInformationForm from './ExtraInformationForm'
import { ScrollRowType } from './types'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  hasPayrollBundle: boolean
  isCurrent: boolean
  readonly?: boolean
  scrollTo?: ScrollRowType | null | undefined
}

const shouldShowTaxResidence = (
  hasPayrollBundle: boolean,
  employee: EmployeeBlocksEmployeeFragment
) => hasPayrollBundle && employee.legalEntity?.country === 'es'

export const TaxResidence = observer(
  ({ employee, hasPayrollBundle, readonly = false, scrollTo }: Props) => {
    const taxResidence = employee.taxResidence

    const form = useRef(
      new TaxResidenceForm({
        employee_id: employee.id,
        is_resident: readonly && !taxResidence ? undefined : taxResidence?.isResident ?? true,
        city_of_birth: taxResidence?.cityOfBirth ?? '',
        country_of_birth: taxResidence?.countryOfBirth ?? '',
      })
    )
    const taxResidenceForm = form.current

    const isSpanish = employee.nationality?.toLowerCase() === 'es'

    const isNotResident =
      taxResidenceForm.get('is_resident').value === false ||
      taxResidenceForm.get('is_resident').value === 'false'

    if (!shouldShowTaxResidence(hasPayrollBundle, employee)) return null

    return (
      <Box gap="s24">
        <BasicForm
          employeeId={employee.id}
          employeeTaxResidence={taxResidence}
          taxResidenceForm={taxResidenceForm}
          readonly={readonly}
          isSpanish={isSpanish}
          scrollTo={scrollTo}
        />
        {isNotResident && (
          <ExtraInformationForm
            employeeId={employee.id}
            employeeTaxResidence={taxResidence}
            readonly={readonly}
            scrollTo={scrollTo}
          />
        )}
      </Box>
    )
  }
)

const TaxResidenceWrapper = (props: Props) => {
  if (!shouldShowTaxResidence(props.hasPayrollBundle, props.employee)) return null

  return (
    <Section
      illustration="sign-post"
      title={i18n.t('employee_form.tax_residence.title')}
      description={i18n.tCurrent('employee_form.tax_residence.description', props.isCurrent)}
    >
      <Box
        gap="s24"
        background="grey200"
        padding="s32"
        width="s360"
        alignSelf="flexEnd"
        borderRadius={{
          all: 'abs004',
        }}
      >
        <TaxResidence {...props} />
      </Box>
    </Section>
  )
}

export default observer(TaxResidenceWrapper)
